import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Clock } from "lucide-react";

const DEFAULT_IDLE_MIN = 15;
const DEFAULT_ABSOLUTE_H = 8;

export function SessionTimeout() {
  const { user, logout } = useAuth();
  const lastActivity = useRef(Date.now());
  const loginStart = useRef(Date.now());
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(60);
  // Per-tenant session-timeout config (audit item 82). Default 15 min idle,
  // 8 hr absolute per HIPAA best practice; admins can tune from Settings.
  const [cfg, setCfg] = useState({ idleMs: DEFAULT_IDLE_MIN * 60 * 1000,
                                    absoluteMs: DEFAULT_ABSOLUTE_H * 60 * 60 * 1000 });

  useEffect(() => {
    if (!user) return;
    api.get("/settings/session-timeout").then((r) => {
      const idle = Math.max(1, Math.min(240, Number(r.data.idle_minutes) || DEFAULT_IDLE_MIN));
      const abs = Math.max(1, Math.min(24, Number(r.data.absolute_hours) || DEFAULT_ABSOLUTE_H));
      setCfg({ idleMs: idle * 60 * 1000, absoluteMs: abs * 60 * 60 * 1000 });
    }).catch(() => {});
  }, [user]);

  const reset = useCallback(() => { lastActivity.current = Date.now(); setWarning(false); }, []);

  useEffect(() => {
    if (!user) return;
    loginStart.current = Date.now();
    const bump = () => { if (!warning) lastActivity.current = Date.now(); };
    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const warnAt = cfg.idleMs - 60 * 1000;
    const timer = setInterval(() => {
      const idle = Date.now() - lastActivity.current;
      const totalSession = Date.now() - loginStart.current;
      // Absolute cap: force sign-out after N hours regardless of activity.
      if (totalSession >= cfg.absoluteMs) {
        clearInterval(timer);
        setWarning(false);
        logout();
        return;
      }
      if (idle >= cfg.idleMs) {
        clearInterval(timer);
        setWarning(false);
        logout();
      } else if (idle >= warnAt) {
        setWarning(true);
        setSecondsLeft(Math.max(0, Math.ceil((cfg.idleMs - idle) / 1000)));
      }
    }, 1000);
    return () => { events.forEach((e) => window.removeEventListener(e, bump)); clearInterval(timer); };
  }, [user, warning, logout, cfg]);

  if (!warning) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="session-timeout-modal">
      <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FFF3E0] text-[#B84A00]"><Clock size={22} /></span>
          <div>
            <h3 className="font-heading text-lg font-bold text-[#3E2723]">Still there?</h3>
            <p className="text-sm text-[#5D4037]">For your security, you'll be signed out in <span className="font-bold text-[#B84A00]" data-testid="timeout-countdown">{secondsLeft}s</span> due to inactivity.</p>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button data-testid="timeout-logout-btn" onClick={logout} className="rounded-full px-4 py-2 text-sm font-semibold text-[#6D4C41] hover:bg-[#F5F2EB]">Sign out now</button>
          <button data-testid="timeout-stay-btn" onClick={reset} className="rounded-full bg-[#4E342E] px-5 py-2 text-sm font-semibold text-white hover:bg-[#3E2723]">Stay signed in</button>
        </div>
      </div>
    </div>
  );
}
